import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { UserService } from 'src/app/modules/admin/user-management/user.service';
import { UserModel } from 'src/app/modules/admin/user-management/user-model';
import { RoleServiceService } from 'src/app/modules/admin/role-management/role-service.service';
import { RoleModel } from 'src/app/modules/admin/role-management/role.model';
import { ShareService, ShareViewModel } from '../../services/share.service';
import { Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { NbToastrService } from '@nebular/theme';

export interface ShareDataSettingsData {
  currentSetting?: string;
  isPublic?: boolean;
  specificUsers?: {id: string, name: string}[];
  specificRoles?: {id: string, name: string}[];
  itemId: string;
  itemType: string;
  onSave?: (success: boolean) => void;
}

@Component({
  selector: 'app-share-data-settings',
  templateUrl: './share-data-settings.component.html',
  styleUrls: ['./share-data-settings.component.scss']
})
export class ShareDataSettingsComponent implements OnInit, OnDestroy {
  shareOption: string = 'private';
  specificUsers: {id: string, name: string, isLoading: boolean}[] = [];
  specificRoles: {id: string, name: string}[] = [];
  
  availableRoles: RoleModel[] = [];
  isLoadingRoles: boolean = false;
  isSaving: boolean = false;
  
  searchTerm: string = '';
  userSuggestions: UserModel[] = [];
  showSuggestions: boolean = false;
  private searchTerms = new Subject<string>();
  private destroy$ = new Subject<void>();
  
  constructor(
    public dialogRef: MatDialogRef<ShareDataSettingsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareDataSettingsData,
    private userService: UserService,
    private roleService: RoleServiceService,
    private shareService: ShareService,
    private toastrService: NbToastrService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    if (this.data) {
      // Set the correct share option based on the data
      if (this.data.isPublic) {
        this.shareOption = 'public';
      } else if (this.data.specificUsers && this.data.specificUsers.length > 0) {
        this.shareOption = 'specific-users';
      } else if (this.data.specificRoles && this.data.specificRoles.length > 0) {
        this.shareOption = 'specific-roles';
      } else {
        this.shareOption = 'private';
      }
      
      this.specificUsers = this.data.specificUsers.map(x => ({...x, isLoading: true})) || [];
      this.specificRoles = this.data.specificRoles || [];
      
      // Load user details for the already mapped users
      this.loadUserDetails();
    }
    
    // Load roles from API
    this.loadRoles();
    
    // Set up search with debounce
    this.searchTerms.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
    ).subscribe(term => {
      if (term.trim().length > 0) {
        this.searchUsers(term);
      } else {
        this.userSuggestions = [];
        this.showSuggestions = false;
      }
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  loadRoles(): void {
    this.isLoadingRoles = true;
    this.roleService.getAllRoles().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response.result) {
          this.availableRoles = response.result;
          
          // Update role names for already selected roles
          if (this.specificRoles.length > 0) {
            this.specificRoles.forEach(selectedRole => {
              const matchingRole = this.availableRoles.find(role => role.id === selectedRole.id);
              if (matchingRole) {
                selectedRole.name = matchingRole.displayName || selectedRole.id;
              }
            });
          }
        }
      },
      error: (err) => {
        console.error('Error loading roles:', err);
      },
      complete: () => {
        this.isLoadingRoles = false;
      }
    });
  }
  
  loadUserDetails(): void {
    // Only proceed if we have specific users to load
    if (!this.specificUsers || this.specificUsers.length === 0) {
      return;
    }
    
    // Get all user IDs that need to be loaded
    const userIds = this.specificUsers.map(user => user.id).filter(id => id);
    
    // If no valid IDs, exit early
    if (userIds.length === 0) {
      return;
    }
    
    // Load user details for each ID
    userIds.forEach(userId => {
      try {
      this.userService.getUserById(userId).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (response) => {
          if (response && response.result) {
            const user = response.result;
            const userIndex = this.specificUsers.findIndex(u => u.id === userId);
            
            if (userIndex !== -1) {
              // Update the user name with the actual name from the API
              const userName = `${user.userName}` || `${user.firstName || ''} ${user.lastName || ''}`.trim() 
                                                  || user.email || user.id;
              
              this.specificUsers[userIndex].name = userName;
              this.specificUsers[userIndex].isLoading = false;
            }
          }
        },
        error: (err) => {
          console.error(`Error loading user details for ID ${userId}:`, err);
          
          const userIndex = this.specificUsers.findIndex(u => u.id === userId);
          if (userIndex !== -1) {
            this.specificUsers[userIndex].isLoading = false;
          }
        }
      });
    } catch (error) {
      console.error(`Error loading user details for ID ${userId}:`, error);
    }
    });
  }
  
  async onSaveChanges(): Promise<void> {
    const result = {
      shareOption: this.shareOption,
      specificUsers: this.shareOption === 'specific-users' ? this.specificUsers : [],
      specificRoles: this.shareOption === 'specific-roles' ? this.specificRoles : []
    };

    // Create confirmation message based on the selected sharing option
    let confirmMessage = '';
    if (this.shareOption === 'private') {
      confirmMessage = 'Are you sure you want to keep this data private? Only you will be able to access it.';
    } else if (this.shareOption === 'specific-users') {
      const userCount = this.specificUsers.length;
      confirmMessage = `Are you sure you want to share this data with ${userCount} user${userCount !== 1 ? 's' : ''}?`;
    } else if (this.shareOption === 'specific-roles') {
      const roleCount = this.specificRoles.length;
      confirmMessage = `Are you sure you want to share this data with all users who have specific role: <strong>${this.specificRoles.map(role => role.name).join(', ')}</strong>?`;
    } else if (this.shareOption === 'public') {
      confirmMessage = 'Are you sure you want to make this data public? It will be accessible to everyone';
    }

    try {
      // Open confirmation dialog and wait for result
      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        width: '400px',
        data: {
          message: confirmMessage,
          yesTitle: 'Confirm',
          falseTitle: 'Cancel'
        },
        panelClass: 'custom-z-index-confirm'
      });

      const confirmed = await dialogRef.afterClosed().toPromise();
      if (confirmed) {
        await this.saveShareSettings(result);
      }
    } catch (error) {
      console.error('Error in confirmation dialog:', error);
    }
  }
  
  async saveShareSettings(result: any): Promise<void> {
    if (!this.data.itemId || !this.data.itemType) {
      console.error('Missing required item information for sharing');
      return;
    }
    
    this.isSaving = true;
    
    try {
      // Prepare the API request model
      const shareModel: ShareViewModel = {
        itemId: this.data.itemId,
        itemType: this.data.itemType,
        sharedWithUserId: '',
        sharedWithRoleId: '',
        isPublic: this.shareOption === 'public'
      };
      
      // Set user IDs or role IDs based on the selected option
      if (this.shareOption === 'specific-users' && this.specificUsers.length > 0) {
        shareModel.sharedWithUserId = this.specificUsers.map(user => user.id).join(',');
      } else if (this.shareOption === 'specific-roles' && this.specificRoles.length > 0) {
        shareModel.sharedWithRoleId = this.specificRoles.map(role => role.id).join(',');
      }
      
      // Call the API and await response
      const response = await this.shareService.shareItem(shareModel).toPromise();
      const success = response && response.result;

      if (success) {
        this.toastrService.success('Item shared successfully');
      }
      
      // Call the onSave callback if provided
      if (this.data && this.data.onSave) {
        this.data.onSave(success);
      }
      
      // Close the dialog and return the result
      this.dialogRef.close(success);
      
    } catch (error) {
      console.error('Error sharing item:', error);
      this.dialogRef.close(false);
      
      if (this.data && this.data.onSave) {
        this.data.onSave(false);
      }
    } finally {
      this.isSaving = false;
    }
  }

  onSearchInput(event: any): void {
    const value = event.target.value as string;
    this.searchTerm = value;
    this.searchTerms.next(this.searchTerm);
  }
  
  searchUsers(query: string): void {
    if (query.trim() === '') {
      this.userSuggestions = [];
      this.showSuggestions = false;
      return;
    }
    
    this.userService.searchUser(query).subscribe(response => {
      if (response.result) {
        // Filter out users that have already been selected
        this.userSuggestions = response.result.filter(user => {
          // Check by ID instead of name/email
          return !this.specificUsers.some(selectedUser => selectedUser.id === user.id);
        });
        this.showSuggestions = this.userSuggestions.length > 0;
      }
    });
  }
  
  selectUser(user: UserModel): void {
    if (user.id && !this.specificUsers.some(selectedUser => selectedUser.id === user.id)) {
      const userName = `${user.userName}` || `${user.firstName + ' ' + user.lastName}`.trim() || user.email || user.id;
      this.specificUsers.push({
        id: user.id,
        name: userName,
        isLoading: false
      });
      // Reset search term and trigger a new search term event with empty string
      this.searchTerm = '';
      this.searchTerms.next('');
      this.userSuggestions = [];
      this.showSuggestions = false;
    }
  }
  
  addUser(userInput: string): void {
    if (!userInput || userInput.trim() === '') return;
    
    // Check if the entered text matches any user in the search results
    const matchedUser = this.userSuggestions.find(user => {
      const userName = `${user.firstName} ${user.lastName}`.trim();
      const userEmail = user.email || '';
      return userName.toLowerCase().includes(userInput.toLowerCase()) || 
             userEmail.toLowerCase().includes(userInput.toLowerCase());
    });
    
    if (matchedUser) {
      // If there's a match, use the matched user
      this.selectUser(matchedUser);
    } else {
      // Clear the input if no match found
      this.searchTerm = '';
      this.searchTerms.next('');
    }
    
    this.userSuggestions = [];
    this.showSuggestions = false;
  }

  removeUser(userId: string): void {
    this.specificUsers = this.specificUsers.filter(u => u.id !== userId);
    
    // If there's an active search, refresh results to include the removed user
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      // Force a new search by emitting the current search term again
      this.searchTerms.next(this.searchTerm);
    }
  }

  toggleRole(role: RoleModel): void {
    const roleIndex = this.specificRoles.findIndex(r => r.id === role.id);
    
    if (roleIndex === -1) {
      // Role not selected yet, add it
      this.specificRoles.push({
        id: role.id,
        name: role.displayName
      });
    } else {
      // Role already selected, remove it
      this.specificRoles.splice(roleIndex, 1);
    }
  }
  
  isRoleSelected(roleId: string): boolean {
    return this.specificRoles.some(r => r.id === roleId);
  }
  
  removeRole(roleId: string): void {
    this.specificRoles = this.specificRoles.filter(r => r.id !== roleId);
  }
  
  hideSuggestions(): void {
    // Small delay to allow click events to complete
    setTimeout(() => {
      this.showSuggestions = false;
    }, 200);
  }
}
